import { faArrowTurnDownRight, faArrowUpRightFromSquare, faCheck, faLaptop, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Loader from '@sharedComponents/Loader';
import SearchBar from '@sharedComponents/SearchBar';
import Typography from '@sharedComponents/Typography';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPlatformTypeLabel, orderPlatformsProductionFirst } from '../../helpers';
import { RESTRICTED } from '../accessRules';
import { targetKey } from '../overrideRules';
import { configuredTargets, groupByParent, highlightSegments } from '../targetSearchRules';

import * as style from './style.module.css';

const MIN_TERM_LENGTH = 2;
const NO_ANSWER = { term: null, items: [], total: 0, failed: false };
const INDENTS = [null, style.indent1, style.indent2];

const noClients = async () => ({ items: [], total: 0 });
const noAddition = async () => {};

// Text with the parts that match the search term in bold.
const Highlighted = ({ text, term, color }) => (
    <>
        {highlightSegments(text, term).map((segment, index) =>
            segment.match ? (
                <Typography key={index} span fontWeight="bold" color={color}>
                    {segment.text}
                </Typography>
            ) : (
                <Fragment key={index}>{segment.text}</Fragment>
            ),
        )}
    </>
);

// One line of the results, in columns: action, name, reference, counts and link. The line starts further
// right at each level, below level 1 with an arrow, while the reference, counts and link columns stay aligned.
const ResultRow = ({ level, present, action, icon = null, name, reference = null, counts, link }) => {
    const tone = present ? 'neutral-tertiary' : 'neutral-secondary';
    return (
        <FlexBox role="listitem" alignItems="center" gap="xs" className={style.row}>
            <FlexBox
                justifyContent="center"
                alignItems="center"
                className={`${style.iconCell} ${INDENTS[level - 1] ?? ''} ${present ? style.present : ''}`}
            >
                {action}
            </FlexBox>
            <FlexBox alignItems="center" gap="xs" flexGrow={1} flexShrink={1} flexBasis={0} className={style.nameCell}>
                {level > 1 && <FontAwesomeIcon icon={faArrowTurnDownRight} className={style.mutedIcon} />}
                {icon && <FontAwesomeIcon icon={icon} className={style.mutedIcon} />}
                <Typography color={present ? 'neutral-tertiary' : 'neutral-primary'}>{name}</Typography>
            </FlexBox>
            <Typography fontSize="s" color={tone} className={style.reference}>
                {reference}
            </Typography>
            <Typography fontSize="s" color={tone} className={style.counts}>
                {counts}
            </Typography>
            <FlexBox justifyContent="center" alignItems="center" className={style.iconCell}>
                {link}
            </FlexBox>
        </FlexBox>
    );
};

/**
 * Search of a client, a sub-client or a platform to add to the collection: listed in a public collection,
 * authorized in a restricted one. The results are grouped by parent, each client followed by its platforms
 * (`client.platforms`). The targets that already have a row of `overrides` are checked.
 * `searchClients(term, { signal })` resolves to `{ items, total }` and must be stable. `onAdd({ kind, uuid,
 * name })` must not reject: the "+" of that target is disabled until it settles.
 */
const TargetSearch = ({ visibility, overrides = [], disabled = false, searchClients = noClients, onAdd = noAddition }) => {
    const { t } = useTranslation('bo');
    const [term, setTerm] = useState('');
    // Last answer, tagged with its term: while another term is searched, nothing of the previous one is shown.
    const [answer, setAnswer] = useState(NO_ANSWER);
    const [adding, setAdding] = useState([]);

    const restricted = visibility === RESTRICTED;
    const searching = term.length >= MIN_TERM_LENGTH;
    const answered = searching && answer.term === term;
    const entries = answered && !answer.failed ? groupByParent(answer.items) : [];
    const targets = configuredTargets(overrides);

    useEffect(() => {
        if (term.length < MIN_TERM_LENGTH) return undefined;
        const controller = new AbortController();
        searchClients(term, { signal: controller.signal }).then(
            ({ items = [], total = 0 }) => {
                if (!controller.signal.aborted) setAnswer({ term, items, total, failed: false });
            },
            () => {
                if (!controller.signal.aborted) setAnswer({ ...NO_ANSWER, term, failed: true });
            },
        );
        return () => controller.abort();
    }, [term, searchClients]);

    const add = (target) => {
        const key = targetKey(target);
        if (adding.includes(key)) return undefined;
        setAdding((current) => [...current, key]);
        return onAdd(target).finally(() => setAdding((current) => current.filter((pending) => pending !== key)));
    };

    // The "+" of a target, which stays the same, disabled, element once the target is in the list: the focus
    // is kept after an addition.
    const actionOf = (target, present) => {
        let label = t('Add {{name}}', { name: target.name });
        if (present) label = t('{{name}} is already in the list', { name: target.name });
        else if (restricted) label = t('Authorize {{name}}', { name: target.name });
        return (
            <Button
                ghost
                icon={present ? faCheck : faPlus}
                title={label}
                aria-label={label}
                disabled={present || disabled || adding.includes(targetKey(target))}
                action={() => add(target)}
            />
        );
    };

    const linkTo = (path, name) => {
        const label = t('Open the page of {{name}}', { name });
        return (
            <Button
                ghost
                icon={faArrowUpRightFromSquare}
                title={label}
                aria-label={label}
                action={`${import.meta.env.BASE_URL}${path}`}
                target="_blank"
                role="link"
            />
        );
    };

    const nameOf = (text, present, suffix = '') => (
        <>
            <Highlighted text={text} term={term} color={present ? 'neutral-tertiary' : 'neutral-primary'} />
            {suffix}
            {present && ` · ${t('already in the list')}`}
        </>
    );

    const countsOf = (client) => {
        const counts =
            client.subClientCount > 0
                ? [
                      t('{{count}} sub-client(s)', { count: client.subClientCount }),
                      t('{{count}} platform(s)', { count: client.platformCount + client.subClientsPlatformCount }),
                  ]
                : [t('{{count}} platform(s)', { count: client.platformCount })];
        const inList = client.platforms.filter((platform) => targets.platforms.has(platform.uuid)).length;
        if (inList > 0) counts.push(t('{{count}} in the list', { count: inList }));
        return counts.join(' · ');
    };

    const platformRows = (client, level) =>
        orderPlatformsProductionFirst(client.platforms).map((platform) => {
            const present = targets.platforms.has(platform.uuid);
            return (
                <ResultRow
                    key={`platform-${platform.uuid}`}
                    level={level}
                    present={present}
                    action={actionOf({ kind: 'platform', uuid: platform.uuid, name: platform.url }, present)}
                    icon={faLaptop}
                    name={nameOf(platform.url, present)}
                    counts={getPlatformTypeLabel(t, platform.type)}
                    link={linkTo(`clients/${client.uuid}/platforms/${platform.uuid}`, platform.url)}
                />
            );
        });

    const rows = entries.flatMap(({ client, level, parentName }) => {
        const present = targets.clients.has(client.uuid);
        return [
            <ResultRow
                key={`client-${client.uuid}`}
                level={level}
                present={present}
                action={actionOf({ kind: 'client', uuid: client.uuid, name: client.name }, present)}
                name={nameOf(client.name, present, parentName ? ` (${parentName})` : '')}
                reference={client.subClientCount > 0 ? null : client.reference}
                counts={countsOf(client)}
                link={linkTo(`clients/${client.uuid}`, client.name)}
            />,
            ...platformRows(client, level + 1),
        ];
    });

    // Outcome of the search, announced by screen readers: the results themselves stay out of the live region.
    let message = null;
    if (searching && !answered) {
        message = <Loader />;
    } else if (answered && answer.failed) {
        message = <Typography color="error-primary">{t('The search failed. Please try again.')}</Typography>;
    } else if (answered && rows.length === 0) {
        message = <Typography color="neutral-secondary">{t('No client or platform matches this search.')}</Typography>;
    } else if (answered && answer.total > answer.items.length) {
        message = (
            <Typography fontSize="s" color="neutral-secondary">
                {t('Only the first {{shown}} clients are shown: refine the search.', { shown: answer.items.length })}
            </Typography>
        );
    }

    return (
        <FlexBox flexDirection="column" className={style.frame}>
            <SearchBar
                embedded
                hideMenu
                compact
                minChars={0}
                debounceMs={300}
                controlShouldRenderValue={false}
                /* eslint-disable-next-line jsx-a11y/no-autofocus */
                autoFocus={false}
                search={async () => []}
                onTextChange={(text) => setTerm(text.trim())}
                onClear={() => setTerm('')}
                disabled={disabled}
                placeholder={
                    restricted
                        ? t('Authorize a client or a platform: search by name, reference or URL')
                        : t('Add a client or a platform: search by name, reference or URL')
                }
                ariaLabel={restricted ? t('Authorize a client or a platform') : t('Add a client or a platform')}
            />
            {rows.length > 0 && (
                <FlexBox flexDirection="column" className={style.results}>
                    <FlexBox role="list" flexDirection="column" className={style.list}>
                        {rows}
                    </FlexBox>
                </FlexBox>
            )}
            <FlexBox role="status" aria-live="polite" flexDirection="column" className={style.status}>
                {message}
            </FlexBox>
        </FlexBox>
    );
};

export default TargetSearch;
